import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  BackfillAnalysis,
  BackfillListItem,
  BackfillListQuery,
  BackfillProcessorStatus,
  BackfillStats,
  archiveBackfillRow,
  disableBackfillProcessor,
  enableBackfillProcessor,
  getBackfillAnalysis,
  getBackfillProcessorStatus,
  getBackfillRow,
  getBackfillStats,
  listBackfillRows,
  promoteBackfillRow,
  runBackfillProcessor,
} from '../api/backfill';
import { PageHeader, Card, Button, StatusPill } from './ui';
import './AdminBacklog.css';

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  classifying: 'Classifying',
  auto_handled: 'Auto-handled',
  promoted: 'Promoted',
  error: 'Error',
  skipped: 'Skipped',
};

const ACTION_LABELS: Record<string, string> = {
  AUTO_ARCHIVE: 'Auto-archive',
  AUTO_ASSIGN_COMPLETE: 'Auto-assign+complete',
  AUTO_ASSIGN_ONLY: 'Auto-assign',
  MANUAL_REVIEW: 'Manual review',
};

const AdminBacklog: React.FC = () => {
  const { isAdmin } = useAuth();

  const [status, setStatus] = useState<BackfillProcessorStatus | null>(null);
  const [stats, setStats] = useState<BackfillStats | null>(null);
  const [analysis, setAnalysis] = useState<BackfillAnalysis | null>(null);
  const [rows, setRows] = useState<BackfillListItem[]>([]);
  const [total, setTotal] = useState(0);

  const [query, setQuery] = useState<BackfillListQuery>({
    page: 1,
    pageSize: 50,
    sort: 'createdAt',
    dir: 'desc',
  });

  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [batchSize, setBatchSize] = useState(25);
  const [detail, setDetail] = useState<any>(null);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, st, an, list] = await Promise.all([
        getBackfillProcessorStatus(),
        getBackfillStats(),
        getBackfillAnalysis(),
        listBackfillRows(query),
      ]);
      setStatus(s);
      setStats(st);
      setAnalysis(an);
      setRows(list.items);
      setTotal(list.total);
    } catch (err: any) {
      console.error('Failed to load backfill data', err);
      setError(err?.response?.data?.error || 'Failed to load backfill data');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (isAdmin) refreshAll();
  }, [isAdmin, refreshAll]);

  const refreshList = useCallback(async () => {
    try {
      const list = await listBackfillRows(query);
      setRows(list.items);
      setTotal(list.total);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load list');
    }
  }, [query]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / (query.pageSize || 50))),
    [total, query.pageSize]
  );

  if (!isAdmin) {
    return <div className="bfl-forbidden">Admin access required.</div>;
  }

  const updateFilter = (patch: Partial<BackfillListQuery>) => {
    setQuery(prev => ({ ...prev, page: 1, ...patch }));
  };

  const handleEnable = async () => {
    setMessage('');
    try {
      await enableBackfillProcessor();
      setMessage('Processor enabled.');
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to enable');
    }
  };

  const handleDisable = async () => {
    setMessage('');
    try {
      await disableBackfillProcessor();
      setMessage('Processor disabled.');
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to disable');
    }
  };

  const handleRun = async () => {
    setMessage('');
    setRunning(true);
    try {
      const res = await runBackfillProcessor(batchSize);
      const r = res.results || {};
      setMessage(
        `Batch: ${r.totalProcessed ?? 0} processed — ` +
        `${r.autoArchived ?? 0} archived, ${r.autoCompleted ?? 0} auto-completed, ` +
        `${r.promoted ?? 0} promoted, ${r.totalErrors ?? 0} errors.`
      );
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to run processor');
    } finally {
      setRunning(false);
    }
  };

  const handlePromote = async (id: string) => {
    if (!window.confirm('Force-promote this row into the staff task list?')) return;
    try {
      await promoteBackfillRow(id);
      setMessage(`Row ${id} promoted.`);
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to promote');
    }
  };

  const handleArchive = async (id: string) => {
    const reason = window.prompt('Archive reason?', 'Manually archived by admin');
    if (!reason) return;
    try {
      await archiveBackfillRow(id, reason);
      setMessage(`Row ${id} archived.`);
      refreshAll();
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to archive');
    }
  };

  const handleView = async (id: string) => {
    try {
      const row = await getBackfillRow(id);
      setDetail(row);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load row');
    }
  };

  const statusCount = (key: string) => status?.statusCounts?.[key] ?? 0;
  const bucketCount = (key: string) => analysis?.buckets?.[key] ?? 0;

  return (
    <div>
      <PageHeader
        title="CAF Backfill Backlog"
        description="Segregated view for the CAF Pre-Processor Backlog reconciliation. Rows are AI-classified and either auto-handled or promoted to the staff task list."
      />

      {error && <div className="bfl-alert err">{error}</div>}
      {message && <div className="bfl-alert ok">{message}</div>}

      {/* Stats cards */}
      <Card className="bfl-section">
        <div className="bfl-section-title">Processor status</div>
        <div className="bfl-grid">
          <div className="bfl-stat total">
            <div className="bfl-stat-value">{(stats?.total ?? 0).toLocaleString()}</div>
            <div className="bfl-stat-label">Total</div>
          </div>
          <div className="bfl-stat pending">
            <div className="bfl-stat-value">{statusCount('pending').toLocaleString()}</div>
            <div className="bfl-stat-label">Pending</div>
          </div>
          <div className="bfl-stat classifying">
            <div className="bfl-stat-value">{statusCount('classifying').toLocaleString()}</div>
            <div className="bfl-stat-label">Classifying</div>
          </div>
          <div className="bfl-stat auto_handled">
            <div className="bfl-stat-value">{statusCount('auto_handled').toLocaleString()}</div>
            <div className="bfl-stat-label">Auto-handled</div>
          </div>
          <div className="bfl-stat promoted">
            <div className="bfl-stat-value">{statusCount('promoted').toLocaleString()}</div>
            <div className="bfl-stat-label">Promoted</div>
          </div>
          <div className="bfl-stat error">
            <div className="bfl-stat-value">{statusCount('error').toLocaleString()}</div>
            <div className="bfl-stat-label">Error</div>
          </div>
        </div>

        <div className="bfl-toolbar">
          <span className="bfl-scheduler-label">
            Scheduler: <StatusPill status={status?.enabled ? 'running' : 'disabled'} label={status?.enabled ? 'ENABLED' : 'DISABLED'} />
            {status?.jobActive && status?.nextRun && (
              <span> — next run {new Date(status.nextRun).toLocaleString()}</span>
            )}
          </span>
          {status?.enabled ? (
            <Button variant="danger" onClick={handleDisable}>Disable scheduler</Button>
          ) : (
            <Button variant="primary" onClick={handleEnable}>Enable scheduler</Button>
          )}
          <span className="bfl-batchsize-label">Batch size:</span>
          <input
            type="number"
            value={batchSize}
            onChange={e => setBatchSize(Math.max(1, Math.min(500, parseInt(e.target.value || '25', 10))))}
            className="bfl-batchsize-input"
          />
          <Button variant="primary" onClick={handleRun} disabled={running}>
            {running ? 'Running…' : 'Run one batch now'}
          </Button>
          <Button variant="secondary" onClick={refreshAll} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </Button>
        </div>
      </Card>

      {/* Client vs lender analysis */}
      {analysis && (
        <Card className="bfl-section">
          <div className="bfl-section-title">Sender analysis — client vs lender vs internal</div>
          <div className="bfl-grid4">
            <div className="bfl-stat client">
              <div className="bfl-stat-value">{bucketCount('client').toLocaleString()}</div>
              <div className="bfl-stat-label">Client</div>
            </div>
            <div className="bfl-stat lender">
              <div className="bfl-stat-value">{bucketCount('lender').toLocaleString()}</div>
              <div className="bfl-stat-label">Lender</div>
            </div>
            <div className="bfl-stat internal">
              <div className="bfl-stat-value">{bucketCount('internal').toLocaleString()}</div>
              <div className="bfl-stat-label">Internal (complexlaw)</div>
            </div>
            <div className="bfl-stat unclassified">
              <div className="bfl-stat-value">{bucketCount('unclassified').toLocaleString()}</div>
              <div className="bfl-stat-label">Unclassified</div>
            </div>
          </div>

          {(() => {
            const total = Object.values(analysis.buckets || {}).reduce((a, b) => a + b, 0) || 1;
            const seg = (k: string) => {
              const v = bucketCount(k);
              const pct = (v / total) * 100;
              if (pct < 0.5) return null;
              return (
                <div key={k} className={`bfl-bar-seg bfl-bar-seg--${k}`} style={{ width: `${pct}%` }}>
                  {pct >= 5 ? `${k} ${pct.toFixed(0)}%` : ''}
                </div>
              );
            };
            return (
              <div className="bfl-dist-wrap">
                <div className="bfl-dist-label">Distribution</div>
                <div className="bfl-bar">
                  {seg('client')}
                  {seg('lender')}
                  {seg('internal')}
                  {seg('unclassified')}
                </div>
              </div>
            );
          })()}

          <div className="bfl-cols2">
            <div>
              <div className="bfl-section-title">Top 20 senders</div>
              <table className="bfl-table">
                <thead><tr><th>From</th><th className="bfl-right">Count</th></tr></thead>
                <tbody>
                  {analysis.topSenders.map(s => (
                    <tr key={s.from}>
                      <td className="bfl-break-all">{s.from}</td>
                      <td className="bfl-right">{s.count.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div>
              <div className="bfl-section-title">Top 20 lenders (classified)</div>
              <table className="bfl-table">
                <thead><tr><th>Lender</th><th className="bfl-right">Count</th></tr></thead>
                <tbody>
                  {analysis.topLenders.map(l => (
                    <tr key={l.lender}>
                      <td>{l.lender}</td>
                      <td className="bfl-right">{l.count.toLocaleString()}</td>
                    </tr>
                  ))}
                  {analysis.topLenders.length === 0 && (
                    <tr><td colSpan={2} className="bfl-muted">No classified lenders yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      )}

      {/* Action/category breakdown */}
      {stats && (
        <Card className="bfl-section">
          <div className="bfl-section-title">Classifier actions</div>
          <table className="bfl-table bfl-table-narrow">
            <thead><tr><th>Action</th><th className="bfl-right">Count</th></tr></thead>
            <tbody>
              {Object.entries(stats.actionCounts)
                .sort((a, b) => b[1] - a[1])
                .map(([k, v]) => (
                  <tr key={k}>
                    <td>{ACTION_LABELS[k] || k}</td>
                    <td className="bfl-right">{v.toLocaleString()}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Filters + table */}
      <Card className="bfl-section">
        <div className="bfl-section-title">Backfill rows ({total.toLocaleString()})</div>
        <div className="bfl-filters">
          <select
            value={query.processorStatus || ''}
            onChange={e => updateFilter({ processorStatus: e.target.value || undefined })}
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_LABELS).map(k => (
              <option key={k} value={k}>{STATUS_LABELS[k]}</option>
            ))}
          </select>
          <select
            value={query.processorAction || ''}
            onChange={e => updateFilter({ processorAction: e.target.value || undefined })}
          >
            <option value="">All actions</option>
            {Object.keys(ACTION_LABELS).map(k => (
              <option key={k} value={k}>{ACTION_LABELS[k]}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search from / subject"
            value={query.q || ''}
            onChange={e => setQuery(prev => ({ ...prev, q: e.target.value }))}
            onKeyDown={e => { if (e.key === 'Enter') refreshList(); }}
            className="bfl-search-input"
          />
          <select
            value={query.sort || 'createdAt'}
            onChange={e => updateFilter({ sort: e.target.value })}
          >
            <option value="createdAt">Sort: created</option>
            <option value="email_date">Sort: email date</option>
            <option value="processor_status">Sort: status</option>
            <option value="processor_action">Sort: action</option>
          </select>
          <select
            value={query.dir || 'desc'}
            onChange={e => updateFilter({ dir: e.target.value as 'asc' | 'desc' })}
          >
            <option value="desc">Desc</option>
            <option value="asc">Asc</option>
          </select>
          <Button variant="secondary" onClick={refreshList}>Apply</Button>
        </div>

        <div className="bfl-table-wrap">
          <table className="bfl-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Action</th>
                <th>From</th>
                <th>Subject</th>
                <th>Lender</th>
                <th>Client</th>
                <th>Ref</th>
                <th>Email date</th>
                <th>Conf.</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td>
                    <span className={`bfl-pill ${r.processorStatus}`}>
                      {STATUS_LABELS[r.processorStatus] || r.processorStatus}
                    </span>
                  </td>
                  <td>{r.processorAction ? (ACTION_LABELS[r.processorAction] || r.processorAction) : '—'}</td>
                  <td className="bfl-cell-from">{r.emailFrom || '—'}</td>
                  <td className="bfl-ellipsis" title={r.emailSubject || ''}>
                    {r.emailSubject || '—'}
                  </td>
                  <td>{r.aiLenderName || '—'}</td>
                  <td>{r.aiClientName || '—'}</td>
                  <td>{r.aiClaimReference || '—'}</td>
                  <td>{r.emailDate ? new Date(r.emailDate).toLocaleDateString() : '—'}</td>
                  <td>{r.aiConfidence != null ? r.aiConfidence.toFixed(2) : '—'}</td>
                  <td className="bfl-nowrap">
                    <Button variant="secondary" size="sm" onClick={() => handleView(r.id)}>View</Button>
                    {r.processorStatus !== 'promoted' && (
                      <Button variant="primary" size="sm" onClick={() => handlePromote(r.id)}>Promote</Button>
                    )}
                    {r.processorStatus !== 'promoted' && r.processorStatus !== 'auto_handled' && (
                      <Button variant="danger" size="sm" onClick={() => handleArchive(r.id)}>Archive</Button>
                    )}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="bfl-empty-row">No rows.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bfl-pagination">
          <span>Page {query.page} of {totalPages}</span>
          <div>
            <Button
              variant="secondary"
              size="sm"
              disabled={(query.page || 1) <= 1}
              onClick={() => setQuery(prev => ({ ...prev, page: Math.max(1, (prev.page || 1) - 1) }))}
            >Prev</Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={(query.page || 1) >= totalPages}
              onClick={() => setQuery(prev => ({ ...prev, page: (prev.page || 1) + 1 }))}
            >Next</Button>
          </div>
        </div>
      </Card>

      {detail && (
        <div className="bfl-modal-backdrop" onClick={() => setDetail(null)}>
          <div className="bfl-modal" onClick={e => e.stopPropagation()}>
            <div className="bfl-modal-header">
              <h3>Row {detail.id}</h3>
              <Button variant="secondary" size="sm" onClick={() => setDetail(null)}>Close</Button>
            </div>
            <div className="bfl-modal-meta">
              <div><strong>From:</strong> {detail.email_from}</div>
              <div><strong>Subject:</strong> {detail.email_subject}</div>
              <div><strong>Date:</strong> {detail.email_date}</div>
              <div><strong>Status:</strong> {detail.processor_status} / {detail.processor_action || '—'}</div>
              <div><strong>Promoted task:</strong> {detail.promoted_task_id || '—'}</div>
            </div>
            {detail.ai_reasoning && (
              <div className="bfl-modal-block">
                <strong>AI reasoning:</strong>
                <pre className="bfl-pre-wrap">{detail.ai_reasoning}</pre>
              </div>
            )}
            {detail.email_body && (
              <div className="bfl-modal-block">
                <strong>Body:</strong>
                <pre className="bfl-pre-scroll">{detail.email_body}</pre>
              </div>
            )}
            {detail.processor_result_json && (
              <div>
                <strong>Processor result:</strong>
                <pre>{JSON.stringify(detail.processor_result_json, null, 2)}</pre>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBacklog;
